import { useId, useState, type ReactNode } from "react";
import { CoachWeeklyTimingSchema, EmailUpdatePurposeSchema, NO_EMAIL_CHOICES, type CoachWeeklyTiming, type EmailChoices, type EmailFunctionalPurpose, type EmailUpdatePurpose } from "@pushback-and-chaos/contracts";
import { CONSENT, EMAIL_CHOICES, FUNCTIONAL_EMAIL_ASKS } from "../design/copy.js";
import { Pill } from "../ui/primitives.js";

/**
 * What the desk card and settings are given: a signed-in learner's saved
 * choices and whether their address may receive mail (Andre's ruling 17,
 * 2026-10-12), or a guest's empty, disabled ones.
 */
export type EmailChoicesFor = { readonly signedIn: true; readonly addressVerified: boolean; readonly choices: EmailChoices } | { readonly signedIn: false };

const isEmpty = (c: EmailChoices): boolean => EmailUpdatePurposeSchema.options.every((purpose) => !c[purpose]);

/**
 * A draft carried across a change in what the server holds (Codex's review,
 * 2026-10-07): every choice the learner has not touched since `base` takes the
 * newly saved value, so a box withdrawn on the other surface is never left
 * checked to be granted again by the next save; every choice they have
 * touched keeps their edit. The weekly note and its timing move as one.
 */
export function rebaseEmailDraft(draft: EmailChoices, base: EmailChoices, saved: EmailChoices): EmailChoices {
  const next: EmailChoices = { ...draft };
  for (const purpose of EmailUpdatePurposeSchema.options) {
    if (purpose !== "COACH_WEEKLY" && draft[purpose] === base[purpose]) next[purpose] = saved[purpose];
  }
  if (draft.COACH_WEEKLY === base.COACH_WEEKLY && draft.coachWeeklyTiming === base.coachWeeklyTiming) {
    next.COACH_WEEKLY = saved.COACH_WEEKLY;
    next.coachWeeklyTiming = saved.coachWeeklyTiming;
  }
  return next;
}

/**
 * THE SEVEN EMAIL CHOICES (2026-10-07), one body for the desk card and for
 * settings, so both show the same saved choices with the same words and
 * behaviour. Every box starts as the server last saved it, and empty for
 * anyone who has said nothing; neither weekly timing is chosen in advance;
 * "save choices." and "no emails." sit side by side with equal weight, and
 * "no emails." saves every update purpose off in one action. A weekly note
 * needs a timing before it can be saved, so the ledger never holds one
 * nobody chose. A guest sees every box empty and disabled, and "sign in."
 * in place of the two controls. A signed-in learner whose address is not
 * verified reads one sentence under the hold notice (ruling 17, 2026-10-12):
 * mail goes only to a verified address. The choices still save.
 */
export function EmailChoicesFields({
  state,
  busy,
  seasonSet,
  onSave,
  onSignIn,
}: {
  state: EmailChoicesFor;
  busy: boolean;
  /** Whether the learner's recruiting season is set; season-only choices say where to set it when it is not. */
  seasonSet: boolean;
  onSave: (choices: EmailChoices) => Promise<void>;
  onSignIn: () => void;
}): ReactNode {
  const enabled = state.signedIn;
  const saved = state.signedIn ? state.choices : NO_EMAIL_CHOICES;
  const [draft, setDraft] = useState<EmailChoices>(saved);
  // What the server held when the draft was last reconciled; when a save on
  // the other surface (or a re-read) changes it, the draft follows it.
  const [base, setBase] = useState<EmailChoices>(saved);
  // The set this form last sent. A saved set that is not it came from the other
  // surface, and this form's confirmation no longer describes what is saved.
  const [sent, setSent] = useState<EmailChoices | null>(null);
  const [confirmed, setConfirmed] = useState<string | null>(null);
  if (JSON.stringify(saved) !== JSON.stringify(base)) {
    setBase(saved);
    setDraft(rebaseEmailDraft(draft, base, saved));
    if (JSON.stringify(saved) !== JSON.stringify(sent)) setConfirmed(null);
  }
  const [failed, setFailed] = useState<string | null>(null);
  const timingName = useId();

  const set = (next: EmailChoices): void => {
    setDraft(next);
    setConfirmed(null);
    setFailed(null);
  };
  const toggle = (purpose: EmailUpdatePurpose, on: boolean): void => {
    // Turning the weekly note off forgets its timing; turning it on chooses none.
    set(purpose === "COACH_WEEKLY" ? { ...draft, COACH_WEEKLY: on, coachWeeklyTiming: null } : { ...draft, [purpose]: on });
  };
  const save = (choices: EmailChoices): void => {
    setDraft(choices);
    setSent(choices);
    setConfirmed(null);
    setFailed(null);
    // The confirmation is only for a save that landed; a refusal is said here, in the words it came with.
    void onSave(choices).then(
      () => setConfirmed(isEmpty(choices) ? EMAIL_CHOICES.savedNone : EMAIL_CHOICES.saved),
      (caught: unknown) => setFailed(caught instanceof Error && caught.message !== "" ? caught.message : EMAIL_CHOICES.notSaved),
    );
  };
  const weeklyIncomplete = draft.COACH_WEEKLY && draft.coachWeeklyTiming === null;
  const needsSeason = !seasonSet && (draft.SEASON_REMINDERS || draft.coachWeeklyTiming === "RECRUITING_SEASON");

  return (
    <div data-testid="email-choices-fields">
      <p className="mt-4 mb-0 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.holdNotice}</p>
      {state.signedIn && !state.addressVerified ? <p data-testid="email-unverified" className="mt-2 mb-0 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.unverifiedAddress}</p> : null}
      {EmailUpdatePurposeSchema.options.map((purpose) => {
        const { label, hint } = EMAIL_CHOICES.purposes[purpose];
        return (
          <div key={purpose} data-purpose={purpose}>
            <label className={["mt-5 flex items-start gap-4", enabled ? "cursor-pointer" : "cursor-not-allowed"].join(" ")}>
              <input type="checkbox" checked={enabled && draft[purpose]} disabled={busy || !enabled} onChange={(e) => toggle(purpose, e.target.checked)} className="mt-1" />
              <span>
                <span className="block font-sans text-body">{label}</span>
                <span className="mt-1 block font-sans text-micro text-ink-soft">{hint}</span>
              </span>
            </label>
            {purpose === "COACH_WEEKLY" && (draft.COACH_WEEKLY || !enabled) ? (
              <div role="radiogroup" aria-label={label} className="mt-3 ml-8">
                {CoachWeeklyTimingSchema.options.map((timing: CoachWeeklyTiming) => (
                  <label key={timing} className={["mt-2 flex items-center gap-3", enabled ? "cursor-pointer" : "cursor-not-allowed"].join(" ")}>
                    <input type="radio" name={timingName} value={timing} checked={enabled && draft.coachWeeklyTiming === timing} disabled={busy || !enabled} onChange={() => set({ ...draft, coachWeeklyTiming: timing })} />
                    <span className="font-sans text-body">{EMAIL_CHOICES.weeklyOptions[timing]}</span>
                  </label>
                ))}
                <p className="mt-2 mb-0 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.weeklyNote}</p>
              </div>
            ) : null}
            {purpose === "RESEARCH_INVITES" ? <p className="mt-2 mb-0 ml-8 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.researchNote}</p> : null}
          </div>
        );
      })}
      {enabled && weeklyIncomplete ? <p className="mt-4 mb-0 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.weeklyTimingNeeded}</p> : null}
      {needsSeason ? <p className="mt-4 mb-0 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.seasonNeeded}</p> : null}
      <p className="mt-4 mb-0 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.functionalNote}</p>
      {enabled ? null : <p className="mt-4 mb-0 font-sans text-micro text-ink-soft">{CONSENT.guestNote}</p>}
      <div className="mt-5 flex flex-wrap gap-3">
        {enabled ? (
          <>
            <Pill disabled={busy || weeklyIncomplete} onClick={() => save(draft)}>{EMAIL_CHOICES.save}</Pill>
            <Pill disabled={busy} onClick={() => save(NO_EMAIL_CHOICES)}>{EMAIL_CHOICES.none}</Pill>
          </>
        ) : (
          <Pill onClick={onSignIn}>{EMAIL_CHOICES.signIn}</Pill>
        )}
      </div>
      {confirmed === null ? null : <p role="status" className="mt-3 mb-0 font-sans text-micro">{confirmed}</p>}
      {failed === null ? null : <p role="alert" className="mt-3 mb-0 font-sans text-micro">{failed}</p>}
    </div>
  );
}

/**
 * The card on the desk, opened (Codex's section 2): the title, the intro and
 * the permanence note above the choices. Collapsing is the lightbox's own
 * control, worded "collapse card."; it records nothing.
 */
export function EmailChoicesCard(props: Parameters<typeof EmailChoicesFields>[0]): ReactNode {
  return (
    <div data-testid="email-choices-card">
      <h3 className="mt-0 mb-0 font-sans text-body">{EMAIL_CHOICES.title}</h3>
      <p className="mt-2 mb-0 font-sans text-body">{props.state.signedIn ? EMAIL_CHOICES.intro : EMAIL_CHOICES.guestIntro}</p>
      <EmailChoicesFields {...props} />
      <p className="mt-5 mb-0 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.permanenceNote}</p>
    </div>
  );
}

/**
 * A JUST-IN-TIME ASK (Codex's section 3), for certificates or results: the
 * question, and two answers with neither chosen. Built and tested; nothing
 * renders it yet, because no such email exists and sending is held.
 */
export function FunctionalEmailAsk({ purpose, busy, onAnswer }: { purpose: EmailFunctionalPurpose; busy: boolean; onAnswer: (granted: boolean) => void }): ReactNode {
  const ask = FUNCTIONAL_EMAIL_ASKS[purpose];
  return (
    <div data-testid="functional-email-ask" data-purpose={purpose}>
      <p className="mt-0 mb-0 font-sans text-body">{ask.question}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Pill disabled={busy} onClick={() => onAnswer(true)}>{ask.yes}</Pill>
        <Pill disabled={busy} onClick={() => onAnswer(false)}>{ask.no}</Pill>
      </div>
    </div>
  );
}
